import { Link, useSearch } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { streakMultiplier } from "@/lib/game-utils";

interface GameShellProps {
  title: string;
  numeral: string;
  children: ReactNode;
  score: number;
  round: number;
  /** Omit for open-ended (endless) modes. */
  total?: number;
  streak?: number;
  timeLimit?: number;
  timeKey?: string | number;
  onTimeUp?: () => void;
  /** Optional slug for the "Back to hub" link. */
  hubSlug?: string;
}

export function GameShell({
  title,
  numeral,
  children,
  score,
  round,
  total,
  streak = 0,
  timeLimit,
  timeKey,
  onTimeUp,
  hubSlug,
}: GameShellProps) {
  const search = useSearch({ strict: false }) as { live?: string };
  const live = search.live === "1";
  const [remaining, setRemaining] = useState(timeLimit ?? 0);
  const calledRef = useRef(false);
  const mult = streakMultiplier(streak);

  useEffect(() => {
    if (!timeLimit) return;
    calledRef.current = false;
    setRemaining(timeLimit);
    const start = Date.now();
    const id = setInterval(() => {
      const left = Math.max(0, timeLimit - Math.floor((Date.now() - start) / 1000));
      setRemaining(left);
      if (left === 0 && !calledRef.current) {
        calledRef.current = true;
        onTimeUp?.();
        clearInterval(id);
      }
    }, 200);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeKey, timeLimit]);

  return (
    <div className="min-h-screen">
      <header className="border-b border-border/60 sticky top-0 z-30 backdrop-blur bg-background/80">
        <div className={`mx-auto flex items-center justify-between gap-4 px-4 py-3 ${live ? "max-w-6xl" : "max-w-4xl"}`}>
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2 group">
              <span className="font-display text-gold text-xl">Mr. B</span>
              <span className="hidden sm:inline text-xs uppercase tracking-[0.25em] text-muted-foreground group-hover:text-foreground transition">Quotes</span>
            </Link>
            {hubSlug && (
              <Link
                to="/games/$slug"
                params={{ slug: hubSlug }}
                className="hidden sm:inline text-xs text-muted-foreground hover:text-foreground transition border-l border-border pl-3"
              >
                ← Hub
              </Link>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs">
            <div className="hidden sm:flex items-center gap-1.5 rounded-full border border-border px-3 py-1">
              <span className="text-muted-foreground">Round</span>
              <span className="font-mono text-gold">{total ? `${round}/${total}` : `${round} · ∞`}</span>
            </div>
            {streak > 0 && (
              <div className={`flex items-center gap-1 rounded-full border px-2.5 py-1 font-mono ${streak >= 3 ? "border-error/50 text-error animate-pulse" : "border-border text-muted-foreground"}`}>
                {streak >= 3 && <span>🔥</span>}
                <span>{streak}</span>
                {mult > 1 && <span className="text-gold ml-1">×{mult}</span>}
              </div>
            )}
            <div className="flex items-center gap-1.5 rounded-full border border-gold/40 bg-gold/10 px-3 py-1">
              <span className="text-muted-foreground hidden sm:inline">Score</span>
              <span className="font-mono text-gold font-bold">{score}</span>
            </div>
            {timeLimit ? (
              <div className={`rounded-full border px-3 py-1 font-mono ${remaining <= 5 ? "border-error text-error animate-pulse" : "border-border text-foreground"}`}>
                {remaining}s
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <main className={`mx-auto px-4 py-8 ${live ? "max-w-6xl py-12" : "max-w-4xl"}`}>
        <div className="mb-6 flex items-baseline justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Game {numeral}</div>
            <h1 className={`font-display text-gold ${live ? "text-5xl sm:text-6xl" : "text-2xl sm:text-3xl"}`}>{title}</h1>
          </div>
          {live && <span className="rounded-full bg-error/20 text-error border border-error/40 px-3 py-1 text-xs uppercase tracking-widest">● Live</span>}
        </div>
        <div className={live ? "text-lg sm:text-xl" : ""}>{children}</div>
      </main>
    </div>
  );
}

export function useLiveMode() {
  const search = useSearch({ strict: false }) as { live?: string };
  return search.live === "1";
}
